import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Outlet, Navigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api, { clearAuthToken, getAuthToken } from '@/lib/api';
import { toast } from 'sonner';
import {
  LayoutGrid, Users, Dumbbell, Settings, ClipboardList, ChevronDown, Check, BarChart3, HelpCircle,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import HelpModal from '@/components/HelpModal';
import { ThemeProvider } from '@/context/ThemeContext';
import { DemoProvider, useIsDemo } from '@/context/DemoContext';
import { useDemoGuard, DemoBanner } from '@/components/DemoGuard';
import ForcePasswordChange from '@/pages/ForcePasswordChange';
import PlayerLayout from '@/components/PlayerLayout';
import FanLayout from '@/components/FanLayout';

/* ── Design tokens ── */
const C = {
  orange: '#F5A623', orangeDark: '#D4891A',
  navy: '#0D1B26', navyCard: '#1A2B3C', navyBorder: '#243448',
  green: '#27AE60', red: '#E74C3C', blue: '#2980B9',
  muted: '#8FA3B1', textSec: '#A8BFCE',
  border: '#DDE5EC', bg: '#F0F3F6',
};
const SIDEBAR_W = 220;

/* ── Sub-components ── */
/**
 * Age divisions surfaced in the team create + edit dropdowns.
 * Youth tiers 8U–17U, followed by school-level tiers. Order matters
 * — preserved as-is in the menu.
 */
export const AGE_DIVISIONS = [
  '8U', '9U', '10U', '11U', '12U', '13U', '14U', '15U', '16U', '17U',
  'Freshman', 'JV', 'Varsity',
];

function NavItem({ icon, label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`nav-${label.toLowerCase().replace(/[^a-z]/g, '-')}`}
      className={`relative flex items-center gap-2.5 w-full text-left px-5 py-2.5 text-[18px] font-bold uppercase tracking-[.06em] transition-colors ${
        active ? 'text-[#F5A623] bg-[rgba(245,166,35,.08)]' : 'text-[#A8BFCE] hover:text-white hover:bg-white/5'
      }`}
      style={{ fontFamily: "'Barlow Condensed', sans-serif", border: 'none', background: active ? 'rgba(245,166,35,.08)' : 'none' }}
    >
      {active && <span className="absolute left-0 top-1 bottom-1 w-[3px] rounded-r" style={{ background: C.orange }} />}
      {icon}
      {label}
    </button>
  );
}

function TabItem({ icon, label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`tab-${label.toLowerCase()}`}
      className="flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors active:opacity-80"
      style={{
        fontFamily: "'Barlow Condensed', sans-serif",
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '.08em',
        textTransform: 'uppercase',
        background: 'none',
        border: 'none',
        color: active ? '#ffa133' : 'rgba(255,255,255,0.6)',
      }}
    >
      {icon}
      {label}
    </button>
  );
}

/* ════════════════════════════════════════ */
/*  LAYOUT SHELL (sidebar + topbar + outlet) */
/* ════════════════════════════════════════ */
function CoachLayoutShell({ user }) {
  const isDemo = useIsDemo();
  const demoBlocked = useDemoGuard();
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState(null);
  const [currentTeam, setCurrentTeam] = useState(null);

  /* dialogs */
  const [showCreateTeamDialog, setShowCreateTeamDialog] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamGender, setNewTeamGender] = useState('');     // 'boys' | 'girls'
  const [newTeamAgeDivision, setNewTeamAgeDivision] = useState(''); // '8U'..'17U' | 'Freshman' | 'JV' | 'Varsity'
  const [copyRosterEnabled, setCopyRosterEnabled] = useState(false);
  const [copyRosterFromTeamId, setCopyRosterFromTeamId] = useState('');
  const [creatingTeam, setCreatingTeam] = useState(false);
  const [showCreateSeasonDialog, setShowCreateSeasonDialog] = useState(false);
  const [newSeasonName, setNewSeasonName] = useState('');
  const [creatingSeason, setCreatingSeason] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [currentUser, setCurrentUser] = useState(user);
  const [keyboardOpen, setKeyboardOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const isStaff = user?.role === 'staff';

  const activeNav =
    location.pathname === '/dashboard' ? 'dashboard' :
    location.pathname === '/games' ? 'games' :
    location.pathname.startsWith('/game/') ? 'games' :
    location.pathname === '/teams' ? 'teams' :
    location.pathname === '/roster-management' ? 'teams' : // legacy route
    location.pathname.startsWith('/coach/lab') ? 'lab' :
    location.pathname.startsWith('/practice-lab') ? 'lab' :
    location.pathname === '/stats' ? 'stats' :
    location.pathname === '/settings' ? 'settings' :
    'dashboard';

  const initials = (user?.name || 'C').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  /* ── data fetching ── */
  useEffect(() => { fetchTeams(); }, []);

  /* ── Native keyboard listener: hide bottom nav while the iOS / Android
       keyboard is on screen so submit buttons (e.g. Add Game's submit
       button when "My Player" is selected) can never end up trapped
       behind it. Web is a no-op. ── */
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;
    let showSub;
    let hideSub;
    (async () => {
      const { Keyboard } = await import('@capacitor/keyboard');
      showSub = await Keyboard.addListener('keyboardWillShow', () => setKeyboardOpen(true));
      hideSub = await Keyboard.addListener('keyboardWillHide', () => setKeyboardOpen(false));
    })();
    return () => {
      showSub?.remove?.();
      hideSub?.remove?.();
    };
  }, []);

  useEffect(() => {
    if (selectedTeamId && teams.length > 0) {
      setCurrentTeam(teams.find(t => t.team_id === selectedTeamId) || null);
    }
  }, [selectedTeamId, teams]);

  useEffect(() => {
    if (selectedTeamId) fetchSeasons();
  }, [selectedTeamId]);

  // Sync from location state (when navigating between pages)
  useEffect(() => {
    if (location.state?.teamId && teams.length > 0) {
      const t = teams.find(t => t.team_id === location.state.teamId);
      if (t) setSelectedTeamId(location.state.teamId);
    }
    if (location.state?.seasonId) {
      setSelectedSeasonId(location.state.seasonId);
    }
  }, [location.state, teams]);

  const fetchTeams = async () => {
    try {
      const r = await api.get('/teams');
      const data = Array.isArray(r.data) ? r.data : (r.data.teams || []);
      setTeams(data);
      
      // Use last_active_team_id from user if available, otherwise first team
      if (data.length > 0 && !selectedTeamId) {
        const lastActiveTeamId = user?.last_active_team_id;
        const validTeam = lastActiveTeamId && data.find(t => t.team_id === lastActiveTeamId);
        setSelectedTeamId(validTeam ? lastActiveTeamId : data[0].team_id);
      }
    } catch { toast.error('Failed to fetch teams'); }
  };

  const fetchSeasons = async () => {
    try {
      const r = await api.get(`/teams/${selectedTeamId}/seasons`);
      setSeasons(r.data);
      if (r.data.length > 0 && !selectedSeasonId) setSelectedSeasonId(r.data[0].season_id);
    } catch (e) { console.error('Failed to fetch seasons:', e); }
  };

  // Persist last active team to database when changed
  const handleTeamChange = useCallback(async (newTeamId) => {
    if (newTeamId === 'create_new') {
      setShowCreateTeamDialog(true);
      return;
    }
    setSelectedTeamId(newTeamId);
    setSelectedSeasonId(null);
    
    // Persist to database
    try {
      await api.patch('/users/me/last-active-team', { team_id: newTeamId });
    } catch (e) {
      console.error('Failed to save last active team:', e);
    }
  }, []);

  const handleSeasonChange = (value) => {
    if (value === 'create_new_season') setShowCreateSeasonDialog(true);
    else setSelectedSeasonId(value);
  };

  /* ── handlers ── */
  const handleCreateTeam = async (e) => {
    e.preventDefault();
    if (demoBlocked('create and manage your own teams')) return;
    setCreatingTeam(true);
    try {
      const payload = {
        name: newTeamName,
        gender: newTeamGender || undefined,
        age_division: newTeamAgeDivision || undefined,
      };
      if (copyRosterEnabled && copyRosterFromTeamId) {
        payload.copy_roster_from_team_id = copyRosterFromTeamId;
      }
      const r = await api.post('/teams', payload);
      const copied = r.data?.players_copied || 0;
      toast.success(copied > 0 ? `Team created — ${copied} player${copied === 1 ? '' : 's'} copied` : 'Team created!');
      setShowCreateTeamDialog(false);
      setNewTeamName('');
      setNewTeamGender(''); setNewTeamAgeDivision('');
      setCopyRosterEnabled(false); setCopyRosterFromTeamId('');
      await fetchTeams();
      handleTeamChange(r.data.team_id);
    } catch { toast.error('Failed to create team'); }
    finally { setCreatingTeam(false); }
  };

  const handleCreateSeason = async (e) => {
    e.preventDefault();
    if (demoBlocked('create new seasons for your teams')) return;
    if (!newSeasonName.trim() || !selectedTeamId) return;
    setCreatingSeason(true);
    try {
      const r = await api.post(`/teams/${selectedTeamId}/seasons`, { name: newSeasonName.trim() });
      toast.success(`Season "${newSeasonName}" created!`);
      setShowCreateSeasonDialog(false); setNewSeasonName('');
      await fetchSeasons(); setSelectedSeasonId(r.data.season_id);
    } catch { toast.error('Failed to create season'); }
    finally { setCreatingSeason(false); }
  };

  const selectedTeam = teams.find(t => t.team_id === selectedTeamId);
  const selectedSeason = seasons.find(s => s.season_id === selectedSeasonId);

  /* ── Render ── */
  return (
    <div className="flex h-screen overflow-hidden" style={{ fontFamily: "'Barlow', sans-serif", background: C.bg }}>

      {/* ── SIDEBAR (desktop only) ── */}
      <aside
        data-testid="sidebar"
        className="hidden md:flex fixed top-0 left-0 bottom-0 z-[200] flex-col"
        style={{ width: SIDEBAR_W, background: C.navy }}
      >
        <div className="h-[60px] flex items-center px-5 shrink-0" style={{ borderBottom: `1px solid ${C.navyBorder}` }}>
          <span className="font-bold text-xl uppercase tracking-[.02em] text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            Smarter<span style={{ color: C.orange }}>Hoops</span>
          </span>
        </div>

        {/* Sidebar nav: identical items, order, icons, and labels as the
            mobile bottom nav. */}
        <nav className="flex-1 py-4 overflow-y-auto">
          <NavItem icon={<LayoutGrid className="w-5 h-5 shrink-0" />} label="Dashboard" active={activeNav === 'dashboard'}
            onClick={() => navigate('/dashboard')} />
          <NavItem icon={<ClipboardList className="w-5 h-5 shrink-0" />} label="Games" active={activeNav === 'games'}
            onClick={() => navigate('/games')} />
          <NavItem icon={<Users className="w-5 h-5 shrink-0" />} label="Teams" active={activeNav === 'teams'}
            onClick={() => navigate('/teams')} />
          <NavItem icon={<Dumbbell className="w-5 h-5 shrink-0" />} label="Practice" active={activeNav === 'lab'}
            onClick={() => navigate('/coach/lab')} />
          <NavItem icon={<BarChart3 className="w-5 h-5 shrink-0" />} label="Stats" active={activeNav === 'stats'}
            onClick={() => navigate('/stats')} />
        </nav>
      </aside>

      {/* ── MAIN ── */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0 md:ml-[220px]">

        {/* ── TOPBAR ──
            Portrait (mobile): dark navy bg with the SmarterHoops wordmark
            on the left so the brand bookends top and bottom on every
            screen. The team selector is hoisted into the page body on
            Stats / Practice (the two screens that need team context) via
            the <InlineTeamSelector /> below.

            Landscape (desktop, ≥ md): white bg, team selector in the
            topbar — the brand wordmark already lives in the sidebar. */}
        <header
          className="h-[60px] flex items-center justify-between px-4 md:px-7 sticky top-0 z-[100] shrink-0
                     bg-[#0B1824] md:bg-white"
          style={{ borderBottom: `1px solid ${C.border}` }}
          data-testid="topbar"
        >
          <div className="flex items-center gap-3 min-w-0 flex-1 mr-2">
            {/* MOBILE: brand wordmark */}
            <span
              className="md:hidden font-bold text-xl uppercase tracking-[.02em] text-white whitespace-nowrap"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              data-testid="topbar-brand"
            >
              SMARTER<span style={{ color: '#ffa133' }}>HOOPS</span>
            </span>

            {/* DESKTOP: team selector — hidden when no teams (dashboard CTA
                owns first-team creation), static when 1, dropdown when 2+ */}
            <div className="hidden md:flex items-center gap-2 min-w-0">
              {teams.length === 0 ? null : teams.length === 1 ? (
                <div
                  className="h-9 px-3 flex items-center rounded-[7px] text-[14px] sm:text-[18px] font-bold uppercase tracking-[.03em] whitespace-nowrap truncate max-w-[60vw] sm:max-w-none"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif", background: C.bg, border: `1.5px solid ${C.border}`, color: C.navy }}
                  data-testid="team-display"
                >
                  {selectedTeam ? `${selectedTeam.name}${selectedTeam.level ? ` (${selectedTeam.level})` : ''}` : ''}
                </div>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid="team-switcher-trigger"
                      className="h-9 px-3 flex items-center gap-1.5 rounded-[7px] text-[14px] sm:text-[18px] font-bold uppercase tracking-[.03em] whitespace-nowrap truncate max-w-[60vw] sm:max-w-none transition-colors hover:bg-slate-100 active:scale-[.98]"
                      style={{ fontFamily: "'Barlow Condensed', sans-serif", background: C.bg, border: `1.5px solid ${C.border}`, color: C.navy }}
                    >
                      <span className="truncate">
                        {selectedTeam ? `${selectedTeam.name}${selectedTeam.level ? ` (${selectedTeam.level})` : ''}` : 'Select Team'}
                      </span>
                      <ChevronDown className="w-4 h-4 shrink-0 opacity-70" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-[220px]" data-testid="team-switcher-menu">
                    {teams.map(t => (
                      <DropdownMenuItem
                        key={t.team_id}
                        onSelect={() => handleTeamChange(t.team_id)}
                        data-testid={`team-switcher-item-${t.team_id}`}
                        className="flex items-center justify-between gap-3 cursor-pointer"
                      >
                        <span className="truncate font-semibold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                          {t.name}{t.level ? ` (${t.level})` : ''}
                        </span>
                        {t.team_id === selectedTeamId && <Check className="w-4 h-4 shrink-0" style={{ color: C.orange }} />}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {currentUser?.role !== 'admin' && (
              <button onClick={() => setShowHelp(true)} data-testid="topbar-help"
                aria-label="Help"
                className="w-[34px] h-[34px] rounded-full flex items-center justify-center transition-colors active:scale-[.96]
                           bg-white/10 text-white hover:bg-white/20 border border-white/20
                           md:bg-[var(--c-bg)] md:text-[var(--c-navy)] md:border-[var(--c-border)] md:hover:bg-slate-100"
                style={{ ['--c-bg']: C.bg, ['--c-navy']: C.navy, ['--c-border']: C.border }}>
                <HelpCircle className="w-[17px] h-[17px]" />
              </button>
            )}
            <button onClick={() => navigate('/settings')} data-testid="topbar-settings"
              aria-label="Settings"
              className="w-[34px] h-[34px] rounded-full flex items-center justify-center transition-colors active:scale-[.96]
                         bg-white/10 text-white hover:bg-white/20 border border-white/20
                         md:bg-[var(--c-bg)] md:text-[var(--c-navy)] md:border-[var(--c-border)] md:hover:bg-slate-100"
              style={{ ['--c-bg']: C.bg, ['--c-navy']: C.navy, ['--c-border']: C.border }}>
              <Settings className="w-[17px] h-[17px]" />
            </button>
            {/* Avatar — display-only badge (build 191). Account management
                lives entirely in /settings now; tapping the avatar would
                duplicate that surface. Kept visible for visual identity. */}
            <div data-testid="topbar-avatar"
              aria-hidden="true"
              className="w-[34px] h-[34px] rounded-full flex items-center justify-center text-xs font-extrabold tracking-[.04em] select-none"
              style={{
                fontFamily: "'Barlow Condensed', sans-serif",
                background: '#ffa133',
                color: '#0B1824',
                border: 'none',
              }}>
              {initials}
            </div>
          </div>
        </header>

        {/* ── CONTENT ── */}
        <div
          className="p-5 md:p-7 overflow-y-scroll flex-1 min-h-0 pb-app-nav"
          style={{
            scrollbarGutter: 'stable',
            WebkitOverflowScrolling: 'touch',
            paddingBottom: 'calc(env(safe-area-inset-bottom) + 96px)',
          }}
          data-testid="main-scroll-area"
        >
          {isDemo && (
            <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-lg mb-4 border"
              style={{ background: '#FFF8E8', borderColor: '#F5A623' }}
              data-testid="demo-mode-banner">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="#F5A623" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <p className="text-xs font-semibold" style={{ color: '#D4891A' }}>
                You're in the <strong>Coach Demo</strong> &mdash; explore freely, no data will be saved.
                <a href="/register-coach" className="ml-2 underline font-bold" style={{ color: '#D4891A' }}>Sign up for real</a>
              </p>
            </div>
          )}

          {/* Inline team selector — only on Stats and Practice, the two
              screens that need explicit team context. The portrait
              topbar shows the brand logo instead of the team selector,
              so we hoist the selector into the page body here. Visible
              in both portrait and landscape so the placement is
              consistent across the two routes. */}
          {(activeNav === 'stats' || activeNav === 'lab') && teams.length > 0 && (
            <div className="mb-4 flex items-center gap-2" data-testid="inline-team-selector">
              <span
                className="text-[11px] font-bold uppercase tracking-[.12em] text-slate-500"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                Team
              </span>
              {teams.length === 1 ? (
                <div
                  className="h-9 px-3 flex items-center rounded-[7px] text-[14px] sm:text-[16px] font-bold uppercase tracking-[.03em] whitespace-nowrap truncate max-w-full"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif", background: C.bg, border: `1.5px solid ${C.border}`, color: C.navy }}
                  data-testid="inline-team-display"
                >
                  {selectedTeam ? `${selectedTeam.name}${selectedTeam.level ? ` (${selectedTeam.level})` : ''}` : ''}
                </div>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid="inline-team-switcher-trigger"
                      className="h-9 px-3 flex items-center gap-1.5 rounded-[7px] text-[14px] sm:text-[16px] font-bold uppercase tracking-[.03em] whitespace-nowrap truncate max-w-full transition-colors hover:bg-slate-100 active:scale-[.98]"
                      style={{ fontFamily: "'Barlow Condensed', sans-serif", background: C.bg, border: `1.5px solid ${C.border}`, color: C.navy }}
                    >
                      <span className="truncate">
                        {selectedTeam ? `${selectedTeam.name}${selectedTeam.level ? ` (${selectedTeam.level})` : ''}` : 'Select Team'}
                      </span>
                      <ChevronDown className="w-4 h-4 shrink-0 opacity-70" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-[220px]" data-testid="inline-team-switcher-menu">
                    {teams.map(t => (
                      <DropdownMenuItem
                        key={t.team_id}
                        onSelect={() => handleTeamChange(t.team_id)}
                        data-testid={`inline-team-switcher-item-${t.team_id}`}
                        className="flex items-center justify-between gap-3 cursor-pointer"
                      >
                        <span className="truncate font-semibold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                          {t.name}{t.level ? ` (${t.level})` : ''}
                        </span>
                        {t.team_id === selectedTeamId && <Check className="w-4 h-4 shrink-0" style={{ color: C.orange }} />}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          )}

          <Outlet context={{
            user: currentUser, teams, selectedTeamId, setSelectedTeamId: handleTeamChange,
            seasons, selectedSeasonId, setSelectedSeasonId: handleSeasonChange,
            currentTeam, isStaff, isDemo,
            setShowCreateTeamDialog, setShowCreateSeasonDialog,
            fetchTeams, fetchSeasons,
          }} />

          {/* ── BOTTOM-NAV CLEARANCE SPACER ──
              Physical element with explicit height so the last row of
              every page sits well above the fixed mobile bottom nav.
              130px = 96px breathing room + ~34px iOS home-indicator
              safe-area inset (rounded). Hidden on md+ (no bottom nav). */}
          <div aria-hidden className="md:hidden" style={{ height: 'calc(env(safe-area-inset-bottom) + 130px)', flexShrink: 0 }} />
        </div>
      </div>

      {/* ── BOTTOM TABS (mobile only)
          Hidden while the iOS / Android keyboard is on screen so submit
          buttons inside forms can never end up trapped behind the nav. ── */}
      {!keyboardOpen && (
        <nav className="fixed bottom-0 left-0 right-0 z-[300] md:hidden"
          style={{ background: '#0B1824', borderTop: '1px solid rgba(255,255,255,0.08)', paddingBottom: 'env(safe-area-inset-bottom)' }}
          data-testid="bottom-tabs">
          <div className="flex h-14">
            <TabItem icon={<LayoutGrid className="w-5 h-5" />} label="Dashboard" active={activeNav === 'dashboard'}
              onClick={() => navigate('/dashboard')} />
            <TabItem icon={<ClipboardList className="w-5 h-5" />} label="Games" active={activeNav === 'games'}
              onClick={() => navigate('/games')} />
            <TabItem icon={<Users className="w-5 h-5" />} label="Teams" active={activeNav === 'teams'}
              onClick={() => navigate('/teams')} />
            <TabItem icon={<Dumbbell className="w-5 h-5" />} label="Practice" active={activeNav === 'lab'}
              onClick={() => navigate('/coach/lab')} />
            <TabItem icon={<BarChart3 className="w-5 h-5" />} label="Stats" active={activeNav === 'stats'}
              onClick={() => navigate('/stats')} />
          </div>
        </nav>
      )}

      {/* ═══ DIALOGS ═══ */}
      <Dialog open={showCreateTeamDialog} onOpenChange={(open) => {
        setShowCreateTeamDialog(open);
        if (!open) {
          setNewTeamName('');
          setNewTeamGender(''); setNewTeamAgeDivision('');
          setCopyRosterEnabled(false); setCopyRosterFromTeamId('');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-[18px]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Create new team</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateTeam}>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="teamName" className="text-[14px] font-semibold mb-2 block" style={{ color: '#475569' }}>Team name *</Label>
                <Input id="teamName" value={newTeamName} onChange={(e) => setNewTeamName(e.target.value)} placeholder="e.g., Chicago Bulls" required className="h-12" data-testid="new-team-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[14px] font-semibold mb-2 block" style={{ color: '#475569' }}>Gender *</Label>
                  <Select value={newTeamGender} onValueChange={setNewTeamGender} required>
                    <SelectTrigger className="h-12" data-testid="new-team-gender-select">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="boys">Boys</SelectItem>
                      <SelectItem value="girls">Girls</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[14px] font-semibold mb-2 block" style={{ color: '#475569' }}>Age division *</Label>
                  <Select value={newTeamAgeDivision} onValueChange={setNewTeamAgeDivision} required>
                    <SelectTrigger className="h-12" data-testid="new-team-age-division-select">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {AGE_DIVISIONS.map((d) => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {teams.length > 0 && (
                <div className="rounded-lg p-3" style={{ background: C.bg, border: `1.5px solid ${C.border}` }}>
                  <div className="flex items-start gap-2.5">
                    <Checkbox
                      id="copyRoster"
                      checked={copyRosterEnabled}
                      onCheckedChange={(v) => {
                        const next = !!v;
                        setCopyRosterEnabled(next);
                        if (next && !copyRosterFromTeamId && teams[0]) {
                          setCopyRosterFromTeamId(teams[0].team_id);
                        }
                      }}
                      data-testid="copy-roster-checkbox"
                    />
                    <div className="flex-1 min-w-0">
                      <label htmlFor="copyRoster" className="text-[14px] font-semibold cursor-pointer block" style={{ color: '#0D1B26' }}>
                        Copy roster from existing team?
                      </label>
                      <p className="text-xs mt-0.5" style={{ color: C.muted }}>
                        Duplicates the player list only. Stats don't carry over and the favorite flag resets.
                      </p>
                      {copyRosterEnabled && (
                        <div className="mt-2.5">
                          <Select value={copyRosterFromTeamId} onValueChange={setCopyRosterFromTeamId}>
                            <SelectTrigger className="h-10 bg-white" data-testid="copy-roster-source-select">
                              <SelectValue placeholder="Select source team..." />
                            </SelectTrigger>
                            <SelectContent>
                              {teams.map(t => (
                                <SelectItem key={t.team_id} value={t.team_id}>
                                  {t.name}{t.level ? ` (${t.level})` : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateTeamDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={creatingTeam || !newTeamGender || !newTeamAgeDivision || (copyRosterEnabled && !copyRosterFromTeamId)} data-testid="submit-create-team" className="text-[16px]" style={{ background: '#0B1824', color: '#fff' }}>
                {creatingTeam ? 'Creating...' : 'Create team'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showCreateSeasonDialog} onOpenChange={setShowCreateSeasonDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-[18px] uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Add New Season</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateSeason}>
            <div className="space-y-4 py-4">
              <p className="text-[14px]" style={{ color: '#64748b' }}>
                Create a new season for <span className="font-bold">{selectedTeam?.name}</span>.
              </p>
              <div>
                <Label htmlFor="seasonName" className="text-[14px] font-bold uppercase tracking-wider mb-2 block" style={{ color: '#475569' }}>Season Name *</Label>
                <Input id="seasonName" value={newSeasonName} onChange={(e) => setNewSeasonName(e.target.value)} placeholder="e.g., Spring 2026" required className="h-12" data-testid="new-season-name-input" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowCreateSeasonDialog(false); setNewSeasonName(''); }}>Cancel</Button>
              <Button type="submit" disabled={creatingSeason || !newSeasonName.trim()} data-testid="submit-create-season" className="text-[16px]" style={{ background: C.navy, color: '#fff' }}>
                {creatingSeason ? 'Creating...' : 'Create Season'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <HelpModal
        open={showHelp}
        onOpenChange={setShowHelp}
        accountId={currentUser?.account_id}
      />
    </div>
  );
}

/* ════════════════════════════════════════ */
/*  MAIN EXPORT — Role routing + Layout    */
/* ════════════════════════════════════════ */
export default function CoachLayout() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const token = getAuthToken();
    if (!token) { setLoading(false); return; }
    api.get('/auth/me')
      .then(r => setUser(r.data))
      .catch(() => clearAuthToken())
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F0F3F6' }}>
        <div className="animate-spin h-12 w-12 rounded-full" style={{ border: '4px solid #0D1B26', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (!user) return <Navigate to="/" replace />;
  if (user.must_change_password) return <ForcePasswordChange onSuccess={() => window.location.reload()} />;
  if (!user.role) return <Navigate to="/role-selection" replace />;

  // At /dashboard, non-team-manager roles get their own dashboard
  if (location.pathname === '/dashboard') {
    if (user.role === 'fan') return <FanLayout user={user} />;
    if (user.role === 'player') return <PlayerLayout user={user} />;
    if (user.role === 'admin') return <Navigate to="/admin/dashboard" replace />;
  }

  // Non-team-manager/staff on team manager pages → redirect
  if (user.role !== 'coach' && user.role !== 'team_manager' && user.role !== 'staff') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <ThemeProvider user={user} team={null}>
      <DemoProvider isDemo={!!user?.is_demo && !user?.demo_overwrite}>
        <CoachLayoutShell user={user} />
      </DemoProvider>
    </ThemeProvider>
  );
}
